import { createSlice } from "@reduxjs/toolkit";

export const DELIVERY_ORDERS_STORAGE_KEY = "pos:delivery-orders-state";

export const DELIVERY_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  READY: "ready",
};

const defaultSources = [
  "Website",
  "Zomato",
  "Talabat",
  "Smile",
  "Careem",
  "Other",
];

const buildInitialOrders = () => [
  {
    id: "D-2451",
    source: "Website",
    customerName: "Milan Fernando",
    address: "Palm Jumeirah, Dubai",
    orderNo: "WEB-1002451",
    items: [
      { name: "Chicken Burger", qty: 2 },
      { name: "Fries", qty: 1 },
    ],
    total: 86.0,
    status: DELIVERY_STATUS.PENDING,
    placedAt: new Date(Date.now() - 42 * 60000).toISOString(),
  },
  {
    id: "D-2452",
    source: "Zomato",
    customerName: "Nadim Ahmed",
    address: "JVC, Dubai",
    orderNo: "ZO-552321",
    items: [
      { name: "Margherita Pizza", qty: 1 },
      { name: "Pasta Alfredo", qty: 1 },
    ],
    total: 114.0,
    status: DELIVERY_STATUS.PENDING,
    placedAt: new Date(Date.now() - 35 * 60000).toISOString(),
  },
  {
    id: "D-2453",
    source: "Talabat",
    customerName: "Sara Rizvi",
    address: "Business Bay, Dubai",
    orderNo: "TA-778209",
    items: [{ name: "Grilled Chicken Bowl", qty: 2 }],
    total: 72.0,
    status: DELIVERY_STATUS.ACCEPTED,
    placedAt: new Date(Date.now() - 26 * 60000).toISOString(),
  },
  {
    id: "D-2454",
    source: "Smile",
    customerName: "Faisal Kareem",
    address: "Dubai Marina",
    orderNo: "SM-330118",
    items: [
      { name: "Club Sandwich", qty: 1 },
      { name: "Orange Juice", qty: 2 },
    ],
    total: 68.0,
    status: DELIVERY_STATUS.PENDING,
    placedAt: new Date(Date.now() - 19 * 60000).toISOString(),
  },
  {
    id: "D-2455",
    source: "Careem",
    customerName: "Asha Perera",
    address: "Al Barsha 1",
    orderNo: "CA-998640",
    items: [
      { name: "Beef Burger", qty: 1 },
      { name: "Lemon Mint", qty: 1 },
    ],
    total: 59.0,
    status: DELIVERY_STATUS.READY,
    placedAt: new Date(Date.now() - 12 * 60000).toISOString(),
  },
  {
    id: "D-2456",
    source: "Other",
    customerName: "Rayan Silva",
    address: "Mirdif, Dubai",
    orderNo: "OT-221904",
    items: [{ name: "Paneer Wrap", qty: 3 }],
    total: 93.0,
    status: DELIVERY_STATUS.ACCEPTED,
    placedAt: new Date(Date.now() - 8 * 60000).toISOString(),
  },
];

const buildInitialState = () => ({
  sources: defaultSources,
  orders: buildInitialOrders(),
});

const loadState = () => {
  if (typeof window === "undefined") {
    return buildInitialState();
  }

  try {
    const raw = window.localStorage.getItem(DELIVERY_ORDERS_STORAGE_KEY);
    if (!raw) {
      return buildInitialState();
    }
    const parsed = JSON.parse(raw);
    return {
      ...buildInitialState(),
      ...parsed,
      orders: Array.isArray(parsed?.orders) ? parsed.orders : buildInitialOrders(),
    };
  } catch {
    return buildInitialState();
  }
};

const persist = (state) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(DELIVERY_ORDERS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore persistence errors
  }
};

export const deliveryOrdersSlice = createSlice({
  name: "deliveryOrders",
  initialState: loadState(),
  reducers: {
    acceptDeliveryOrder: (state, action) => {
      const orderId = action.payload;
      const order = state.orders.find((item) => item.id === orderId);
      if (order && order.status === DELIVERY_STATUS.PENDING) {
        order.status = DELIVERY_STATUS.ACCEPTED;
        order.acceptedAt = new Date().toISOString();
        persist(state);
      }
    },

    markDeliveryOrderReady: (state, action) => {
      const orderId = action.payload;
      const order = state.orders.find((item) => item.id === orderId);
      if (order && order.status === DELIVERY_STATUS.ACCEPTED) {
        order.status = DELIVERY_STATUS.READY;
        order.readyAt = new Date().toISOString();
        persist(state);
      }
    },

    hydrateDeliveryFromStorage: (state, action) => {
      const next = action.payload || {};
      state.sources = Array.isArray(next.sources) ? next.sources : state.sources;
      state.orders = Array.isArray(next.orders) ? next.orders : state.orders;
    },
  },

  extraReducers: (builder) => {
    // Delivery orders are persisted to localStorage; drop them with the session
    // so the next user does not inherit them.
    builder.addCase("auth/logout", () => {
      if (typeof window !== "undefined") {
        try {
          window.localStorage.removeItem(DELIVERY_ORDERS_STORAGE_KEY);
        } catch {
          // ignore storage errors
        }
      }

      return buildInitialState();
    });
  },
});

export const {
  acceptDeliveryOrder,
  markDeliveryOrderReady,
  hydrateDeliveryFromStorage,
} = deliveryOrdersSlice.actions;

export const selectDeliveryOrders = (state) => state.deliveryOrders.orders;
export const selectDeliverySources = (state) => state.deliveryOrders.sources;

export default deliveryOrdersSlice.reducer;
